import type {
  KeyboardEvent,
  PropsWithChildren,
  ReactElement,
  ReactNode,
} from 'react'
import {
  Children,
  Component,
  Fragment,
  cloneElement,
  createRef,
  isValidElement,
} from 'react'

import { Divider, MenuList as MuiMenuList } from '@material-ui/core'
import { type ListProps } from '@material-ui/core/List'
import { type ListItemProps } from '@material-ui/core/ListItem'
import { type PopoverOrigin } from '@material-ui/core/Popover'
import { nanoid } from 'nanoid'

import { type IMenuListItemProps } from './MenuListItem'
import { MenuListPopoverStyled } from './styles/MenuListPopoverStyled'
import { type IUIProviderValue, UIContext } from '../../core/UIContext'
import { TransparentIconButton } from '../../elements/Button'
import { IconMore } from '@ui/elements/Icon'

export interface IMenuListProps extends ListProps {
  triggerElement?: ReactElement<any>
  closeOnSelect?: boolean
  anchorOrigin?: PopoverOrigin
  transformOrigin?: PopoverOrigin
  ariaLabel?: string
}

/**
 * MUI's MenuList moves focus only through its direct children, and warns on
 * a Fragment child. Callers group items in a Fragment (a checklist whose items
 * stay open on select), so Fragments are unwrapped after the close-on-select
 * wrapping has been applied to the top-level children.
 */
const flattenFragments = (nodes: ReactNode, keyPrefix = ''): ReactNode[] =>
  Children.toArray(nodes).reduce<ReactNode[]>((flattened, node) => {
    if (!isValidElement<{ children?: ReactNode }>(node)) {
      return [...flattened, node]
    }

    const key = `${keyPrefix}${node.key}`

    return node.type === Fragment
      ? [...flattened, ...flattenFragments(node.props.children, `${key}/`)]
      : [...flattened, keyPrefix ? cloneElement(node, { key }) : node]
  }, [])

/** The items MUI's MenuList can move focus to: direct children with a tabindex. */
const getMenuItems = (list: HTMLElement | null): HTMLElement[] =>
  Array.from(list?.children ?? []).filter(
    (child): child is HTMLElement =>
      child instanceof HTMLElement && child.hasAttribute('tabindex')
  )

const isDisabledItem = (item: HTMLElement) =>
  item.getAttribute('aria-disabled') === 'true'

/**
 * An overflow menu opened from a trigger button (a "..." icon button by
 * default).
 *
 * Keyboard: opening the menu focuses its first enabled item; ArrowUp and
 * ArrowDown move between items (wrapping, and crossing between the scrolling
 * and the sticky items), Home and End jump to the first and last item,
 * Enter or Space activates an item, and Esc or Tab closes the menu.
 * Disabled items stay reachable so their `tooltip` reason can be read.
 * Closing returns focus to the trigger, unless an item moved focus on (into
 * a dialog it opened, say).
 */
export class MenuList extends Component<PropsWithChildren<IMenuListProps>> {
  static defaultProps = {
    open: false,
    closeOnSelect: true,
    anchorOrigin: { horizontal: 'right', vertical: 'top' },
    transformOrigin: { horizontal: 'right', vertical: 'top' },
  }
  static contextType = UIContext
  context: IUIProviderValue

  state = {
    anchorEl: null,
  }

  menuId = `menu-list-${nanoid(10)}`
  scrollListRef = createRef<HTMLUListElement>()
  stickyListRef = createRef<HTMLUListElement>()
  triggerNode: HTMLElement | null = null

  openMenu = (e) => {
    this.triggerNode = e.currentTarget
    this.setState({ anchorEl: e.currentTarget })
  }

  handleClose = () => {
    this.setState({ anchorEl: null })
  }

  handleSelection = (callback) => (e) => {
    if (callback) {
      callback()
    }
    this.handleClose()
  }

  getMenuItems = () => [
    ...getMenuItems(this.scrollListRef.current),
    ...getMenuItems(this.stickyListRef.current),
  ]

  focusInitialItem = () => {
    const items = this.getMenuItems()
    const initialItem = items.find((item) => !isDisabledItem(item)) ?? items[0]

    initialItem?.focus()
  }

  /**
   * Returns focus to the trigger once the menu has finished closing, unless
   * focus has already moved on, e.g. into a dialog an item opened. MUI's own
   * restore runs as the menu starts closing, in the same commit that mounts
   * such a dialog, so the two would compete for focus.
   */
  restoreFocus = (paper: HTMLElement) => {
    const { activeElement, body } = paper.ownerDocument
    const focusIsInMenuOrLost =
      !activeElement || activeElement === body || paper.contains(activeElement)

    if (focusIsInMenuOrLost) {
      this.triggerNode?.focus()
    }
  }

  /**
   * Runs after MUI's MenuList has handled the key. With sticky items, each
   * list stops at its own ends, so this moves focus across the two lists.
   */
  handleListKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.key === 'Tab') {
      event.preventDefault()
      this.handleClose()
      return
    }

    if (!this.stickyListRef.current) return

    const items = this.getMenuItems()
    const currentIndex = items.indexOf(event.target as HTMLElement)
    const focusStayed =
      event.currentTarget.ownerDocument.activeElement === event.target

    if (event.key === 'Home') {
      items[0]?.focus()
    } else if (event.key === 'End') {
      items[items.length - 1]?.focus()
    } else if (focusStayed && currentIndex !== -1) {
      if (event.key === 'ArrowDown') {
        items[(currentIndex + 1) % items.length].focus()
      } else if (event.key === 'ArrowUp') {
        items[(currentIndex - 1 + items.length) % items.length].focus()
      }
    }
  }

  render() {
    const { anchorEl } = this.state
    const open = !!anchorEl

    const { container } = this.context

    let triggerElement = this.props.triggerElement || (
      <TransparentIconButton
        iconComponent={<IconMore />}
        type='button'
        aria-label={this.props.ariaLabel || 'Open menu list'}
      />
    )
    triggerElement = cloneElement(triggerElement, {
      onClick: this.openMenu,
      className: 'menu-list-trigger-element',
      'aria-label': this.props.ariaLabel || 'Open menu list',
      'aria-haspopup': 'menu',
      'aria-expanded': open,
      'aria-controls': open ? this.menuId : undefined,
    })

    const childrenUpdatedProp = Children.map(
      this.props.children as ReactElement<IMenuListItemProps>[],
      (child: ReactElement<IMenuListItemProps>) => {
        if (!child) return child

        if (child.props.closeOnSelect) {
          return cloneElement(child, {
            onClick: this.handleSelection(child.props.onClick),
          })
        } else if (
          (child.props.closeOnSelect === null ||
            child.props.closeOnSelect === undefined) &&
          this.props.closeOnSelect
        ) {
          return cloneElement(child, {
            onClick: this.handleSelection(child.props.onClick),
          })
        } else {
          return child
        }
      }
    )

    const stickyListItems: ReactElement<ListItemProps>[] = []
    const scrollableListItems: ReactElement<ListItemProps>[] = []

    Children.forEach(
      childrenUpdatedProp,
      (child: ReactElement<IMenuListItemProps>) => {
        if (child.props.sticky) {
          stickyListItems.push(child)
        } else {
          scrollableListItems.push(child)
        }
      }
    )

    const hasStickyItems = stickyListItems.length > 0

    // With sticky items the popup holds two lists, so the paper is the one
    // menu and each list is a group inside it.
    const menuAttributes = { role: 'menu', id: this.menuId }

    return (
      <>
        {triggerElement}
        <MenuListPopoverStyled
          className='menu-list'
          open={open}
          anchorEl={anchorEl}
          onClose={this.handleClose}
          anchorOrigin={this.props.anchorOrigin}
          transformOrigin={this.props.transformOrigin}
          container={container}
          PaperProps={hasStickyItems ? menuAttributes : undefined}
          disableRestoreFocus
          TransitionProps={{
            onEntering: this.focusInitialItem,
            onExited: this.restoreFocus,
          }}
        >
          <MuiMenuList
            ref={this.scrollListRef}
            className='menu-list-list'
            variant='menu'
            disabledItemsFocusable
            disableListWrap={hasStickyItems}
            onKeyDown={this.handleListKeyDown}
            style={{
              maxHeight: '200px',
              overflowX: 'hidden',
              overflowY: 'auto',
              paddingTop: '0.75rem',
              paddingBottom: '0.75rem',
            }}
            {...(hasStickyItems ? { role: 'group' } : menuAttributes)}
          >
            {flattenFragments(scrollableListItems)}
          </MuiMenuList>
          {hasStickyItems && (
            <Fragment>
              <Divider />
              <MuiMenuList
                ref={this.stickyListRef}
                className='menu-list-sticky-list'
                variant='menu'
                disabledItemsFocusable
                disableListWrap
                onKeyDown={this.handleListKeyDown}
                style={{ overflow: 'hidden' }}
                role='group'
              >
                {flattenFragments(stickyListItems)}
              </MuiMenuList>
            </Fragment>
          )}
        </MenuListPopoverStyled>
      </>
    )
  }
}
